import api from './axiosInstance';

/**
 * Agente 1 — Intake y Clasificación.
 * Dispara el análisis automático de un caso EEPP a partir del contrato y la
 * Hoja de Atención del cliente, y devuelve el brief estructurado producido
 * por OpenAI.
 */

const EEPP_BUSINESS_AREA_ID = 11;

export const getEscriturasIntakeReport = async (caseId, signal) => {
  try {
    const response = await api.get(`/escrituras/intake/${caseId}`, { signal });
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
};

export const runEscriturasIntake = async (caseId, { force = false } = {}, signal) => {
  const response = await api.post(
    `/escrituras/intake/${caseId}/run`,
    null,
    { params: { force }, signal }
  );
  return response.data;
};

/**
 * Marca un flag del intake (`flags_for_lawyer`) inactivo o lo reactiva.
 *   - active:false → marcar inactiva; active:true → reactivar.
 * Devuelve la lista actualizada de flags inactivos del caso
 * (`[{ titulo, by, at }]`). Sin abort para esta mutación: debe completar en el
 * server aunque el usuario navegue (el caller la gatea con mountedRef).
 */
export const setFlagResolution = async (caseId, { titulo, active }, signal) => {
  const response = await api.patch(
    `/escrituras/intake/${caseId}/flags/resolution`,
    { titulo, active },
    { signal }
  );
  return response.data || [];
};

/**
 * Aplica/limpia el override "proceder bajo responsabilidad" del bloqueo por banderas
 * bloqueantes del caso.
 *   - proceed:true  → reanuda la automatización (el redactor genera en el siguiente tick)
 *                     registrando quién/cuándo (traza legal). La bandera sigue visible.
 *   - proceed:false → vuelve a pausa.
 * Devuelve el estado de bloqueo resultante (`CaseBlockState`). El POST NO se ata a un
 * AbortController de unmount: la decisión debe completar en el server aunque el usuario navegue.
 */
export const setBlockingOverride = async (caseId, proceed, signal) => {
  const response = await api.post(
    `/escrituras/intake/${caseId}/blocking/override`,
    { proceed },
    { signal }
  );
  return response.data;
};

/**
 * Agrega manualmente una escritura nueva a un caso EEPP ya analizado (cuando A1 no
 * la detectó). El backend deriva orden/items/estado a partir del tipo; el body solo
 * lleva writingTypeId. Devuelve { caseId, escrituraId, createdItems, reusedItems, snapshot }.
 * El POST NO se ata a un AbortController de unmount: cerrar el modal no cancela la creación.
 */
export const addManualWriting = async (caseId, { writingTypeId }, signal) => {
  const response = await api.post(
    `/escrituras/intake/${caseId}/writings`,
    { writingTypeId },
    { signal }
  );
  return response.data;
};

/**
 * Trae el checklist persistido del caso (con estado real de cada item: pendiente,
 * recibido, rechazado, no_aplica). Esto refleja lo que Agente 2 ha procesado
 * y NO cambia automáticamente cuando se re-corre el intake — es la fuente de
 * verdad operacional. Devuelve null si no hay checklist (404).
 *
 * `includeCancelled`: cuando true, incluye las escrituras canceladas (la vista EEPP
 * BETA las muestra grises). El flujo del agente lee con el default (false) y nunca
 * las ve.
 */
export const getEscriturasChecklist = async (caseId, { includeCancelled = false } = {}, signal) => {
  try {
    const response = await api.get(`/escrituras/checklist/${caseId}`, {
      params: { includeCancelled },
      signal,
    });
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
};

/**
 * Cancela una escritura del caso: la saca del flujo (no dispara correos, no se
 * redacta, no revive, no afecta a las demás), sin borrarla ni mutar sus items. No
 * se recupera (para "volver", se crea una nueva). Sin body. 204 al éxito; 404 si
 * la escritura no existe / no pertenece al caso; 409 si ya está cancelada o si la
 * cerró la notaría.
 */
export const cancelWriting = async (caseId, escrituraId, signal) => {
  await api.patch(`/escrituras/intake/${caseId}/writings/${escrituraId}/cancel`, null, { signal });
};

/**
 * Actualiza el estado y datos de un item del checklist. Usado por:
 *   - El usuario interno cuando completa un item de tipo "dato" (texto)
 *     desde el frontend (ej. domicilio del cónyuge).
 *   - El flujo de revisión humana cuando se acepta/rechaza un archivo en duda.
 *
 * Payload típico para item de texto:
 *   { estado: 'recibido', observacion: 'Calle Los Olivos 123, Maipú' }
 */
export const updateChecklistItem = async (itemId, payload, signal) => {
  const response = await api.patch(`/escrituras/checklist/items/${itemId}`, payload, { signal });
  return response.data;
};

/**
 * Devuelve una URL pre-firmada de S3 para descargar / previsualizar el archivo
 * adjunto a un item del checklist. Backend: GET /api/escrituras/checklist/items/{itemId}/file-url.
 * Devuelve null si el item no tiene archivo (404 silencioso).
 */
export const getChecklistItemFileUrl = async (itemId, { inline = false } = {}, signal) => {
  try {
    const response = await api.get(`/escrituras/checklist/items/${itemId}/file-url`, {
      params: inline ? { inline: true } : undefined,
      signal,
    });
    return response.data?.url || null;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
};

/**
 * Returns only the EEPP cases of a client. EEPP is identified by:
 *   - businessAreaId === 11
 *   - area === "EEPP" (string from backend)
 *   - groupId === 11 (legacy column on Cases_SAAS, kept for backwards compat)
 */
export const getEscriturasCasesForClient = async (clientId, signal) => {
  const response = await api.get('/General/GetCausasRutBajasByClient', {
    params: { clientId },
    signal,
  });
  const cases = response.data?.cases || [];
  return cases.filter(
    (c) =>
      c.businessAreaId === EEPP_BUSINESS_AREA_ID ||
      (c.area && c.area.toUpperCase() === 'EEPP') ||
      c.groupId === EEPP_BUSINESS_AREA_ID
  );
};
